/** Browser-only drafts for the client My Patients page. */

/** User-facing name for catalog patient_personas (simulation + templates). */
export const ANATOMAGE_PATIENT = 'Anatomage Patient';
export const ANATOMAGE_PATIENTS = 'Anatomage Patients';

export const SEX_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

export const MARITAL_STATUS_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'domestic-partner', label: 'Domestic partner' },
  { value: 'separated', label: 'Separated' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
];

export const VITAL_HINT =
  'Enter ranges as "min-max" (e.g., "100-110") or single values (e.g., "100"). For BP, use "sys/dia-sys/dia" (e.g., "110/68-119/70") or "sys/dia" (e.g., "110/68").';

export const VITAL_FIELDS = [
  { key: 'hr', label: 'HR', unit: 'bpm', placeholder: '100-110', helper: 'e.g., 100-110 or 100' },
  { key: 'bp', label: 'BP', unit: 'mmHg', placeholder: '110/68-119/70', helper: 'e.g., 110/68-119/70' },
  { key: 'so2', label: 'SO2', unit: '%', placeholder: '86-87', helper: 'e.g., 86-87 or 86' },
  { key: 'rr', label: 'RR', unit: 'breaths/min', placeholder: '24', helper: 'e.g., 24-26 or 24' },
  { key: 'temp', label: 'Temp', unit: '°F', placeholder: '98.8', helper: 'e.g., 98.8-99.2 or 98.8' },
];

export const HEALTH_OPTIONAL_FIELD_KEYS = new Set([
  'occupationalExposures',
  'obstetricalHealth',
  'mentalHealth',
]);

export const HEALTH_TEXT_FIELDS = [
  { key: 'substanceUse', label: 'Substance Use' },
  { key: 'chronicIllnesses', label: 'Chronic Illnesses' },
  { key: 'medication', label: 'Medication' },
  { key: 'surgery', label: 'Surgery' },
  { key: 'allergy', label: 'Allergy' },
  { key: 'geneticsFamilyHistory', label: 'Genetics/Family History' },
  { key: 'vaccinationHistory', label: 'Vaccination History' },
  { key: 'functionalHealth', label: 'Functional Health' },
  { key: 'diet', label: 'Diet' },
  { key: 'travelHistory', label: 'Travel History' },
  { key: 'sexualHistory', label: 'Sexual History' },
  { key: 'screenings', label: 'Screenings' },
  { key: 'occupationalExposures', label: 'Occupational Exposures (if applicable)' },
  { key: 'obstetricalHealth', label: 'Obstetrical Health (if applicable)' },
  { key: 'mentalHealth', label: 'Mental Health (if applicable)' },
];

/** ponytail: files stay in memory for this visit only; raise the cap when a server upload exists. */
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

const STORAGE_PREFIX = 'client.myPatients.v1';
const NUMBER = String.raw`\d+(?:\.\d+)?`;
const RANGE_PATTERN = new RegExp(`^${NUMBER}(?:-${NUMBER})?$`);
const BP_PATTERN = /^\d+\/\d+(?:-\d+\/\d+)?$/;

const PERSONAL_FIELDS = ['name', 'sex', 'age', 'job', 'maritalStatus', 'residence', 'characterId'];
const HEALTH_FIELDS = ['heightCm', 'weightKg', ...HEALTH_TEXT_FIELDS.map((field) => field.key)];
const VITAL_KEYS = VITAL_FIELDS.map((field) => field.key);

/** Health tab fields copied from an Anatomage Patient template; other health fields clear on apply. */
export const HEALTH_TEMPLATE_FIELD_KEYS = [
  'heightCm',
  'weightKg',
  'chronicIllnesses',
  'medication',
  'surgery',
  'allergy',
  'geneticsFamilyHistory',
  'vaccinationHistory',
  'functionalHealth',
  'travelHistory',
  'sexualHistory',
  'screenings',
  'occupationalExposures',
];

/** Replace the health tab from a template payload (unmapped fields become blank). */
export function mergeHealthTemplate(templateHealth) {
  const source = templateHealth && typeof templateHealth === 'object' ? templateHealth : {};
  const next = pickText({}, HEALTH_FIELDS);
  HEALTH_TEMPLATE_FIELD_KEYS.forEach((key) => {
    next[key] = asText(source[key]);
  });
  return next;
}

/** Replace mapped pathology fields from a template; stage/complication clear; rows come from caller. */
export function mergePathologyTemplate(templatePathology, {
  imaging = [], labs = [], exams = [], differentials = [],
} = {}) {
  const source = templatePathology && typeof templatePathology === 'object' ? templatePathology : {};
  const vitals = pickText({}, VITAL_KEYS);
  VITAL_KEYS.forEach((key) => {
    vitals[key] = asText(source.vitals?.[key]);
  });
  const resolvedDifferentials = differentials.length
    ? differentials
    : (Array.isArray(source.differentials) ? source.differentials : []).map(normalizeDifferentialRow).filter(Boolean);
  const name = resolvedDifferentials.map((row) => row.name).filter(Boolean).join(', ')
    || asText(source.name);
  return {
    name,
    chiefComplaint: asText(source.chiefComplaint),
    historyOfCurrentIllness: asText(source.historyOfCurrentIllness),
    symptom: asText(source.symptom),
    stage: '',
    complication: '',
    vitals,
    imaging,
    labs,
    exams,
    differentials: resolvedDifferentials,
  };
}

/** Replace the full health tab from a saved My Patient template. */
export function mergeSavedHealthTemplate(templateHealth) {
  const source = templateHealth && typeof templateHealth === 'object' ? templateHealth : {};
  const next = pickText({}, HEALTH_FIELDS);
  HEALTH_FIELDS.forEach((key) => {
    next[key] = asText(source[key]);
  });
  return next;
}

/** Replace the full pathology tab from a saved My Patient template. */
export function mergeSavedPathologyTemplate(templatePathology, {
  imaging = [], labs = [], exams = [], differentials = [],
} = {}) {
  const source = templatePathology && typeof templatePathology === 'object' ? templatePathology : {};
  const vitals = pickText({}, VITAL_KEYS);
  VITAL_KEYS.forEach((key) => {
    vitals[key] = asText(source.vitals?.[key]);
  });
  const resolvedDifferentials = differentials.length
    ? differentials
    : (Array.isArray(source.differentials) ? source.differentials : []).map(normalizeDifferentialRow).filter(Boolean);
  const name = resolvedDifferentials.map((row) => row.name).filter(Boolean).join(', ')
    || asText(source.name);
  return {
    name,
    chiefComplaint: asText(source.chiefComplaint),
    historyOfCurrentIllness: asText(source.historyOfCurrentIllness),
    stage: asText(source.stage),
    complication: asText(source.complication),
    symptom: asText(source.symptom),
    vitals,
    imaging,
    labs,
    exams,
    differentials: resolvedDifferentials,
  };
}

export function storageKey(userId) {
  return `${STORAGE_PREFIX}:${userId || 'unknown'}`;
}

export function createId() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
  } catch {
    // Non-browser test environments fall through to the timestamp id.
  }
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function asText(value) {
  return value == null ? '' : String(value);
}

function canonicalOption(value, options) {
  const text = asText(value).trim();
  if (!text) return '';
  const byValue = options.find((option) => option.value === text);
  if (byValue) return byValue.value;
  const byLabel = options.find((option) => option.label.toLowerCase() === text.toLowerCase());
  return byLabel ? byLabel.value : text;
}

function pickText(source, fields) {
  const out = {};
  fields.forEach((field) => {
    out[field] = asText(source?.[field]);
  });
  return out;
}

/** Same set as the admin investigate / exam result editor. */
export const EVALUATION_OPTIONS = ['Harmful', 'Unnec', 'Neutral', 'Recomm', 'Critical'];

/** Same greens as admin evaluation labels (EditResultDialog / NormalPatientDataDetail). */
export const EVALUATION_TOGGLE_STYLE = {
  Critical: { color: '#1B5E20', selectedBg: 'rgba(27, 94, 32, 0.14)', hoverBg: 'rgba(27, 94, 32, 0.22)' },
  Recomm: { color: '#2E7D32', selectedBg: 'rgba(46, 125, 50, 0.14)', hoverBg: 'rgba(46, 125, 50, 0.22)' },
};

export function evaluationToggleButtonSx(evaluationKey) {
  const style = EVALUATION_TOGGLE_STYLE[evaluationKey] || EVALUATION_TOGGLE_STYLE.Critical;
  return {
    flex: 1,
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 600,
    px: 1,
    color: 'text.disabled',
    bgcolor: 'grey.50',
    borderColor: 'divider',
    '&.MuiToggleButton-root.Mui-selected': {
      color: style.color,
      bgcolor: style.selectedBg,
      fontWeight: 700,
      borderColor: `${style.color} !important`,
      zIndex: 1,
      // Grouped toggles overlap borders; draw the inner edge once on the right segment only.
      '&:not(:last-of-type)': {
        borderRightColor: 'transparent !important',
      },
      '& + .MuiToggleButton-root': {
        borderLeftColor: `${style.color} !important`,
      },
    },
    '&.MuiToggleButton-root.Mui-selected:hover': {
      bgcolor: style.hoverBg,
    },
    '&:hover:not(.Mui-selected)': {
      bgcolor: 'grey.200',
    },
  };
}

export function createImagingRow() {
  return {
    id: createId(),
    name: '',
    objectId: '',
    result: '',
    evaluation: 'Critical',
    fileTag: '',
    fileSource: '',
    fileId: '',
  };
}

export function createLabRow() {
  return {
    id: createId(),
    name: '',
    objectId: '',
    result: '',
    evaluation: 'Critical',
  };
}

export function createExamRow() {
  return {
    id: createId(),
    name: '',
    objectId: '',
    result: '',
    evaluation: 'Critical',
  };
}

export function createDifferentialRow() {
  return {
    id: createId(),
    name: '',
    objectId: '',
  };
}

function customPatientEvaluation(value) {
  const canon = canonicalEvaluation(value);
  if (canon === 'Recomm') return 'Recomm';
  return 'Critical';
}

function canonicalEvaluation(value) {
  const text = asText(value).trim();
  if (!text) return '';
  return EVALUATION_OPTIONS.find((option) => option.toLowerCase() === text.toLowerCase()) || '';
}

function asId(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const text = asText(value).trim();
  return text;
}

function fileSourceOf(value) {
  const text = asText(value).trim().toLowerCase();
  return text === 'anatomage' || text === 'custom' ? text : '';
}

function normalizeImagingRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : createId(),
    name: asText(raw.name || raw.study),
    objectId: asId(raw.objectId),
    result: asText(raw.result != null && raw.result !== '' ? raw.result : raw.findings),
    evaluation: customPatientEvaluation(raw.evaluation),
    fileTag: asText(raw.fileTag),
    fileSource: fileSourceOf(raw.fileSource),
    fileId: asId(raw.fileId),
  };
}

function normalizeLabRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : createId(),
    name: asText(raw.name || raw.test),
    objectId: asId(raw.objectId),
    result: asText(raw.result),
    evaluation: customPatientEvaluation(raw.evaluation),
  };
}

function normalizeExamRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : createId(),
    name: asText(raw.name || raw.exam),
    objectId: asId(raw.objectId),
    result: asText(raw.result != null && raw.result !== '' ? raw.result : raw.findings),
    evaluation: customPatientEvaluation(raw.evaluation),
  };
}

function normalizeDifferentialRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : createId(),
    name: asText(raw.name),
    objectId: asId(raw.objectId),
  };
}

function legacyDifferentialsFromName(name) {
  const text = asText(name).trim();
  if (!text) return [];
  return text.split(',').map((part) => part.trim()).filter(Boolean).map((part) => ({
    id: createId(),
    name: part,
    objectId: '',
  }));
}

export function isImagingCategory(category) {
  const value = asText(category).trim().toLowerCase();
  return value === 'imaging' || value === 'bedside';
}

export function isInvestigateLabCategory(tab, category) {
  return asText(tab).trim() === 'investigate' && /\blab/i.test(asText(category));
}

function catalogOption(object) {
  const name = asText(object?.name).trim();
  if (!name) return null;
  return {
    id: object.id,
    name,
    category: asText(object.category).trim(),
  };
}

export function imagingCatalog(objects) {
  return (Array.isArray(objects) ? objects : [])
    .filter((object) => isImagingCategory(object?.category))
    .map(catalogOption)
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export function labCatalog(objects) {
  return (Array.isArray(objects) ? objects : [])
    .filter((object) => isInvestigateLabCategory(object?.tab, object?.category))
    .map(catalogOption)
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export function examCatalog(objects) {
  return (Array.isArray(objects) ? objects : [])
    .filter((object) => asText(object?.tab).trim().toLowerCase() === 'exam')
    .map(catalogOption)
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export function differentialCatalog(objects) {
  return (Array.isArray(objects) ? objects : [])
    .filter((object) => asText(object?.tab).trim().toLowerCase() === 'differential')
    .map(catalogOption)
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export function matchCatalogOption(options, row) {
  const list = Array.isArray(options) ? options : [];
  const objectId = asText(row?.objectId).trim();
  if (objectId) {
    const byId = list.find((option) => String(option.id) === objectId);
    if (byId) return byId;
  }
  const name = asText(row?.name).trim().toLowerCase();
  if (!name) return null;
  return list.find((option) => option.name.toLowerCase() === name) || null;
}

export function alignCatalogRow(row, options) {
  const match = matchCatalogOption(options, row);
  if (!match) {
    if (!asText(row?.name).trim() && !asText(row?.objectId).trim()) return row;
    return { ...row, name: '', objectId: '' };
  }
  const objectId = String(match.id);
  if (row.name === match.name && String(row.objectId) === objectId) return row;
  return { ...row, name: match.name, objectId };
}

export function createEmptyPatient() {
  return normalizePatient({});
}

export function normalizePatient(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const personal = pickText(source.personal, PERSONAL_FIELDS);
  personal.sex = canonicalOption(personal.sex, SEX_OPTIONS);
  if (personal.sex && !SEX_OPTIONS.some((option) => option.value === personal.sex)) {
    personal.sex = '';
  }
  personal.maritalStatus = canonicalOption(personal.maritalStatus, MARITAL_STATUS_OPTIONS);

  const pathologySource = source.pathology && typeof source.pathology === 'object' ? source.pathology : {};
  const imaging = Array.isArray(pathologySource.imaging)
    ? pathologySource.imaging.map(normalizeImagingRow).filter(Boolean)
    : [];
  const labs = Array.isArray(pathologySource.labs)
    ? pathologySource.labs.map(normalizeLabRow).filter(Boolean)
    : [];
  const exams = Array.isArray(pathologySource.exams)
    ? pathologySource.exams.map(normalizeExamRow).filter(Boolean)
    : [];
  let differentials = Array.isArray(pathologySource.differentials)
    ? pathologySource.differentials.map(normalizeDifferentialRow).filter(Boolean)
    : [];
  if (!differentials.length) {
    differentials = legacyDifferentialsFromName(pathologySource.name);
  }
  const pathologyName = differentials.map((row) => row.name).filter(Boolean).join(', ')
    || asText(pathologySource.name);

  return {
    id: typeof source.id === 'string' && source.id ? source.id : createId(),
    personal,
    health: pickText(source.health, HEALTH_FIELDS),
    pathology: {
      name: pathologyName,
      chiefComplaint: asText(pathologySource.chiefComplaint),
      historyOfCurrentIllness: asText(pathologySource.historyOfCurrentIllness),
      stage: asText(pathologySource.stage),
      complication: asText(pathologySource.complication),
      symptom: asText(pathologySource.symptom),
      vitals: pickText(pathologySource.vitals, VITAL_KEYS),
      imaging,
      labs,
      exams,
      differentials,
    },
  };
}

export function patientLabel(patient) {
  const name = asText(patient?.personal?.name).trim();
  return name || 'Unnamed Patient';
}

export const PATIENT_TAB_IDS = ['personal', 'health', 'pathology'];

export function normalizePatientTab(tab) {
  const id = asText(tab).trim().toLowerCase();
  return PATIENT_TAB_IDS.includes(id) ? id : 'personal';
}

export function myPatientsListPath() {
  return '/my-patients';
}

export function myPatientPath(patientId, tab = 'personal') {
  const id = asText(patientId).trim();
  if (!id) return myPatientsListPath();
  return `${myPatientsListPath()}/${encodeURIComponent(id)}/${normalizePatientTab(tab)}`;
}

export function patientGenderLabel(patient) {
  return SEX_OPTIONS.find((option) => option.value === patient?.personal?.sex)?.label || '';
}

export function patientMeta(patient) {
  const sex = patientGenderLabel(patient);
  const age = asText(patient?.personal?.age).trim();
  return [sex, age ? `Age ${age}` : ''].filter(Boolean).join(' · ');
}

export function patientListSecondary(patient) {
  const job = asText(patient?.personal?.job).trim();
  const residence = asText(patient?.personal?.residence).trim();
  return [job, residence].filter(Boolean).join(' · ');
}

export function fieldElementId(patientId, key) {
  return `patient-${patientId}-${String(key).replace(/\./g, '-')}`;
}

export function tabForField(key) {
  if (String(key).startsWith('health.')) return 'health';
  if (String(key).startsWith('pathology.')) return 'pathology';
  return 'personal';
}

export function sanitizeInteger(value, maxDigits = 3) {
  return asText(value).replace(/\D/g, '').slice(0, maxDigits);
}

export function sanitizeDecimal(value, wholeDigits = 3, fractionDigits = 3) {
  const cleaned = asText(value).replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  const whole = parts[0].slice(0, wholeDigits);
  if (parts.length === 1) return whole;
  return `${whole}.${parts.slice(1).join('').slice(0, fractionDigits)}`;
}

function validateAge(value) {
  const text = asText(value).trim();
  if (!text) return '';
  if (!/^\d+$/.test(text) || Number(text) > 125) {
    return 'Age must be a whole number from 0 to 125.';
  }
  return '';
}

function validateHeight(value) {
  const text = asText(value).trim();
  if (!text) return '';
  if (!/^\d+$/.test(text) || Number(text) < 1 || Number(text) > 300) {
    return 'Height must be a whole number of centimeters from 1 to 300.';
  }
  return '';
}

function validateWeight(value) {
  const text = asText(value).trim();
  if (!text) return '';
  if (!/^\d+(\.\d+)?$/.test(text)) {
    return 'Weight must be a number of kilograms, such as 70.5.';
  }
  const weight = Number(text);
  if (weight <= 0 || weight > 500) {
    return 'Weight must be from 0.1 to 500 kg.';
  }
  return '';
}

function validateOption(value, options, message) {
  const text = asText(value).trim();
  if (!text) return '';
  return options.some((option) => option.value === text) ? '' : message;
}

const VITAL_MESSAGES = {
  hr: 'Heart rate must be a number or min-max range, such as 100-110.',
  so2: 'Oxygen saturation must be a number or min-max range, such as 86-87.',
  rr: 'Respiratory rate must be a number or min-max range, such as 24-26.',
  temp: 'Temperature must be a number or min-max range, such as 98.8-99.2.',
  bp: 'Blood pressure must look like 110/68 or 110/68-119/70.',
};

function validateVital(key, value) {
  const text = asText(value).trim();
  if (!text) return '';
  const pattern = key === 'bp' ? BP_PATTERN : RANGE_PATTERN;
  return pattern.test(text) ? '' : VITAL_MESSAGES[key];
}

function validateRequiredText(value, message) {
  return asText(value).trim() ? '' : message;
}

export function validatePatient(patient) {
  const record = normalizePatient(patient);
  const errors = {};
  const assign = (key, message) => {
    if (message) errors[key] = message;
  };

  assign('personal.sex', validateOption(
    record.personal.sex,
    SEX_OPTIONS,
    'Sex must be Male or Female.'
  ));
  assign('personal.age', validateAge(record.personal.age));
  assign('personal.maritalStatus', validateOption(
    record.personal.maritalStatus,
    MARITAL_STATUS_OPTIONS,
    'Choose a marital status from the list.'
  ));
  const characterId = asText(record.personal.characterId).trim();
  if (characterId && !/^\d+$/.test(characterId)) {
    assign('personal.characterId', 'Choose a character from the gallery.');
  }
  assign('health.heightCm', validateHeight(record.health.heightCm));
  assign('health.weightKg', validateWeight(record.health.weightKg));
  if (!validateHeight(record.health.heightCm)) {
    assign('health.heightCm', validateRequiredText(record.health.heightCm, 'Height is required.'));
  }
  if (!validateWeight(record.health.weightKg)) {
    assign('health.weightKg', validateRequiredText(record.health.weightKg, 'Weight is required.'));
  }
  HEALTH_TEXT_FIELDS.forEach((field) => {
    if (HEALTH_OPTIONAL_FIELD_KEYS.has(field.key)) return;
    assign(
      `health.${field.key}`,
      validateRequiredText(record.health[field.key], `${field.label} is required.`)
    );
  });
  VITAL_KEYS.forEach((key) => {
    assign(`pathology.vitals.${key}`, validateVital(key, record.pathology.vitals[key]));
  });

  const firstKey = Object.keys(errors)[0] || '';
  return {
    errors,
    firstKey,
    tab: firstKey ? tabForField(firstKey) : '',
  };
}

export function attachmentTooLarge(file) {
  return Boolean(file && file.size > MAX_ATTACHMENT_BYTES);
}

export function formatFileSize(bytes) {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function loadPatients(storage, userId) {
  if (!storage) return [createEmptyPatient()];
  try {
    const raw = storage.getItem(storageKey(userId));
    if (!raw) return [createEmptyPatient()];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [createEmptyPatient()];
    return parsed.map(normalizePatient).filter(Boolean);
  } catch {
    return [createEmptyPatient()];
  }
}

export function savePatients(storage, userId, patients) {
  const payload = (Array.isArray(patients) ? patients : []).map(normalizePatient).filter(Boolean);
  storage.setItem(storageKey(userId), JSON.stringify(payload));
  return payload;
}
